import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldAlert, CheckCircle2 } from "lucide-react";

const AGREEMENT_TEXT = `ARMORBATCH GLOBAL — SOFTWARE LICENCE AGREEMENT
ABN: 84 424 228 835
PARTIES: This agreement is between ArmorBatch Global (ABN 84 424 228 835), operated by Matthew Dwyer (Provider) and the subscribing business (Client).
LICENCE: The Provider grants the Client a non-exclusive, non-transferable licence to access and use the ArmorBatch platform for their internal business operations only. The Client may not copy, resell, sublicence, or reverse engineer the software.
SUBSCRIPTION & PAYMENT: Access is provided on a monthly subscription basis. The Client agrees to pay the agreed monthly fee. Access will be suspended if payment is not received within 7 days of the due date.
DATA & PRIVACY: The Client's data remains the property of the Client. The Provider will not share, sell, or disclose Client data to any third party without written consent, except where required by law.
CONFIDENTIALITY: Both parties agree to keep confidential any proprietary information, pricing, processes, or business data shared during the term of this agreement.
INTELLECTUAL PROPERTY: All software, design, systems, and technology within the ArmorBatch platform remain the sole intellectual property of the Provider. This agreement does not transfer any ownership rights to the Client.
TERMINATION: Either party may terminate this agreement with 30 days written notice. The Provider may terminate immediately for non-payment or misuse of the platform.
LIMITATION OF LIABILITY: The Provider is not liable for any indirect or consequential loss arising from use of the platform. Total liability is limited to the amount paid in the preceding month.
GOVERNING LAW: This agreement is governed by the laws of Tasmania, Australia.`;

export default function AgreementGate() {
  const { user, org, refreshOrg } = useAuth();
  const pending = org?.agreement?.status === "sent";
  const canSign = user?.role === "admin" || user?.role === "plant_manager";
  const [form, setForm] = useState({
    signer_name: user?.name || "",
    business_name: org?.name || "",
    signed_date: new Date().toISOString().slice(0, 10),
    agree: false,
  });
  const [tiers, setTiers] = useState([]);
  const [tier, setTier] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!pending || user?.is_super_admin) return;
    api.get("/org/me/agreement").then((r) => {
      setTiers(r.data.tiers || []);
      if (r.data.current_tier) setTier(r.data.current_tier);
    }).catch(() => {});
  }, [pending, user?.is_super_admin]);

  if (!pending || user?.is_super_admin) return null;

  const sign = async () => {
    if (!form.agree) { toast.error("Please tick 'I Agree' to sign."); return; }
    if (!form.signer_name.trim() || !form.business_name.trim() || !form.signed_date) {
      toast.error("Full name, business name and date are all required."); return;
    }
    if (tiers.length > 0 && !tier) { toast.error("Please choose a plan to continue."); return; }
    setSubmitting(true);
    try {
      await api.post("/org/me/agreement/sign", { ...form, tier: tier || null });
      toast.success("Agreement signed — full access unlocked!");
      await refreshOrg();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record your signature.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" data-testid="agreement-gate">
      <div className="w-full max-w-3xl max-h-[92vh] overflow-auto bg-[#0E0F11] border border-[#1E88E5]/40 shadow-2xl">
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center gap-3">
          <ShieldAlert className="w-6 h-6 text-amber-400 shrink-0" />
          <div>
            <div className="text-lg font-black text-[#1E88E5]">Action required — sign your licence agreement</div>
            <div className="text-xs text-zinc-400">Your account has limited access until the ArmorBatch Global Software Licence Agreement is signed.</div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="border border-zinc-800 bg-zinc-950/60 p-5 max-h-[40vh] overflow-auto whitespace-pre-wrap text-sm leading-relaxed text-zinc-200" data-testid="gate-agreement-text">
            {AGREEMENT_TEXT}
          </div>
          {canSign ? (
            <>
              {tiers.length > 0 && (
                <div data-testid="gate-tier-picker">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-400 font-bold mb-2">Choose your plan</div>
                  <div className="grid sm:grid-cols-3 gap-3">
                    {tiers.map((t) => {
                      const sel = tier === t.key;
                      return (
                        <button key={t.key} type="button" data-testid={`gate-tier-${t.key}`} onClick={() => setTier(t.key)}
                          className={`text-left border p-3 transition-colors ${sel ? "border-[#1E88E5] bg-[#1E88E5]/10 ring-1 ring-[#1E88E5]" : "border-zinc-700 hover:border-zinc-500"}`}>
                          <div className="text-sm font-black text-zinc-100">{t.name}</div>
                          <div className="text-xl font-black text-[#1E88E5] mt-0.5">${Number(t.price || 0).toLocaleString()}<span className="text-[10px] text-zinc-400 font-normal">/mo</span></div>
                          <div className="text-[10px] text-zinc-500 mt-0.5">{t.truck_max == null ? `${t.truck_min}+ trucks` : `${t.truck_min}–${t.truck_max} trucks`}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500">Full name</label>
                  <input data-testid="gate-name" value={form.signer_name} onChange={(e) => setForm({ ...form, signer_name: e.target.value })}
                    className="w-full bg-transparent border border-zinc-700 px-3 py-2 text-sm text-zinc-100 focus:border-[#1E88E5] outline-none" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500">Business name</label>
                  <input data-testid="gate-business" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                    className="w-full bg-transparent border border-zinc-700 px-3 py-2 text-sm text-zinc-100 focus:border-[#1E88E5] outline-none" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500">Date</label>
                  <input data-testid="gate-date" type="date" value={form.signed_date} onChange={(e) => setForm({ ...form, signed_date: e.target.value })}
                    className="w-full bg-transparent border border-zinc-700 px-3 py-2 text-sm text-zinc-100 focus:border-[#1E88E5] outline-none" />
                </div>
              </div>
              <label className="flex items-start gap-2 text-sm text-zinc-300 cursor-pointer">
                <input data-testid="gate-agree" type="checkbox" checked={form.agree} onChange={(e) => setForm({ ...form, agree: e.target.checked })} className="mt-1" />
                I confirm I am authorised to sign on behalf of this business and I agree to the terms above.
              </label>
              <button data-testid="gate-sign" onClick={sign} disabled={submitting}
                className="w-full bg-[#1E88E5] hover:bg-[#1976D2] text-white font-bold py-3 uppercase tracking-widest text-sm disabled:opacity-50">
                {submitting ? "Signing…" : "I Agree and Sign"}
              </button>
              <p className="text-[10px] text-zinc-500">Signing records your name, the timestamp and your IP address as proof. Full access is granted immediately.</p>
            </>
          ) : (
            <div className="border border-amber-800 bg-amber-950/30 text-amber-200 p-4 text-sm" data-testid="gate-non-admin">
              Please ask an administrator at your business to sign the agreement to unlock full access.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
